import { Fragment, useEffect } from "react";

import Card from '../Common/Card';
import Button from '../Common/Button';
import Image from "../Common/Image";
import PageDivider from "../Common/PageDivider";
import Typography from "../Common/Typography";

import { Orientation, Position, FlexAlignment, ImageLoading, TypographyVariants, TypographyFonts, getUrlForAssets } from "../Contracts";

import styles from "./CataloguingPage.module.scss";

export const CataloguingPage = (props) => {

  const { cataloguingPageData = {} } = props;

  const {
    flexibleOrderSection = {},
    expertSection = {},
    catalogueCommerceSection = {},
    digitalProductSection = {},
    createYourOwnCustomLayoutSection = {},
    customThemesSection = {},
    beautyInSimplicitySection = {}
  } = cataloguingPageData;

  useEffect(() => {
    const element = document.querySelector(`.${styles.CustomThemesList}`);
    if ('IntersectionObserver' in window && window.innerWidth > 600) {
      const intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio === 1) {
            element.classList.add(styles.CustomThemesListOpen);
          }
        });
      }, { threshold: 1 });
      if (element) {
        intersectionObserver.observe(element);
      }
      return () => {
        intersectionObserver.disconnect();
      };
    }
    if (element) {
      element.classList.add(styles.CustomThemesListOpen);
    }
    return () => {
      if (element) {
        element.classList.remove(styles.CustomThemesListOpen);
      }
    };
  }, []);

  const renderCreateBeautifulCatalogueContainer = () => {
    const { cardTitle, cardSubTitle, cardImageUrl, cardImageUrlFallback, cardImageAlt, cardImageHeight, cardImageWidth, cardDescription, buttonsList = [] } = flexibleOrderSection || {};
    return (
      <section className={`${styles.CreateBeautifulCatalogueContainer} page-section-padding`}>
        <Card
          orientation={Orientation.HORIZONTAL}
          imagePosition={{
            mobile: (Position.LEFT),
            desktop: (Position.RIGHT)
          }}
          alignment={{
            justifyContent: FlexAlignment.FLEX_START,
            alignItems: FlexAlignment.CENTER
          }}
          cardImage={{
            className: styles.CataloguingPageCardImage,
            imageUrl: cardImageUrl,
            imageUrlFallback: cardImageUrlFallback,
            imageAlt: cardImageAlt,
            imageHeight: cardImageHeight,
            imageWidth: cardImageWidth,
            imageLoading: ImageLoading.EAGER
          }}
          cardContent={{
            className: styles.CataloguingPageCardContent,
            title: (
              <Fragment>
                {cardTitle && (
                  <Typography
                    variant={TypographyVariants.HEADING_1}
                    className={styles.CataloguingPageCardSubTitle}
                    style={{ fontSize: '14px', color: 'var(--subHeading)' }}
                    tabletStyle={{ fontSize: '14px' }}
                    mobileStyle={{ fontSize: '14px' }}
                    dangerouslySetInnerHTML={{ __html: cardTitle }}
                  />
                )}
                {cardSubTitle && (
                  <Typography
                    variant={TypographyVariants.HEADING_2}
                    className={styles.CataloguingPageCardTitle}
                    style={{ fontSize: '42px', fontWeight: '700', color: 'var(--black)', lineHeight: '1.3', marginTop: '12px' }}
                    tabletStyle={{ fontSize: '36px' }}
                    mobileStyle={{ fontSize: '30px' }}
                    dangerouslySetInnerHTML={{ __html: cardSubTitle }}
                  />
                )}
              </Fragment>
            ),
            description: (
              <Fragment>
                {cardDescription && (
                  <Typography
                    variant={TypographyVariants.PARAGRAPH}
                    style={{ fontSize: '26px', fontWeight: '400', color: 'var(--cardDescription)', marginTop: '18px' }}
                    tabletStyle={{ fontSize: '24px', marginTop: '18px' }}
                    mobileStyle={{ fontSize: '22px', marginTop: '18px' }}
                    className={styles.CataloguingPageCardDescription}
                    dangerouslySetInnerHTML={{ __html: cardDescription }}
                  />
                )}
                {buttonsList.length > 0 && (
                  <div className={styles.CataloguingPageCardButtonsContainer}>
                    {buttonsList.map((button, index) => {
                      const { type, htmlType, linkUrl, openInNewTab, text, onClick } = button;
                      return (
                        <Button key={index} type={type} htmlType={htmlType} className={styles.CataloguingPageCardButton} linkUrl={linkUrl} openInNewTab={openInNewTab} onClick={onClick}>{text}</Button>
                      );
                    })}
                  </div>
                )}
              </Fragment>
            )
          }
          }
          className={styles.CataloguingPageCard}
        />
      </section>
    );
  };

  const renderCompleteFlexibilityWithCatalogueCommerceContainer = () => {
    const { sectionTitle, sectionSubTitle, featuresList = [] } = catalogueCommerceSection || {};
    return (
      <section className={`${styles.CompleteFlexibilityWithCatalogueCommerceContainer} page-section-padding`}>
        {sectionTitle && (
          <Typography
            variant={TypographyVariants.HEADING_2}
            style={{ fontSize: '42px', fontWeight: '700', fontFamily: TypographyFonts.LIBRE_BODONI, fontStyle: 'italic', color: 'var(--black)', marginTop: '24px', textAlign: 'center' }}
            tabletStyle={{ fontSize: '36px', marginTop: '12px' }}
            mobileStyle={{ fontSize: '30px', marginTop: '0px' }}
            dangerouslySetInnerHTML={{ __html: sectionTitle }}
          />
        )}
        {sectionSubTitle && (
          <Typography
            variant={TypographyVariants.HEADING_3}
            style={{ fontSize: '25px', fontFamily: TypographyFonts.AVENIR_NEXT, color: 'var(--black)', marginTop: '36px', marginBottom: '56px', textAlign: 'center' }}
            tabletStyle={{ fontSize: '23px', marginTop: '24px', marginBottom: '48px' }}
            mobileStyle={{ fontSize: '21px', marginTop: '24px', marginBottom: '36px' }}
            dangerouslySetInnerHTML={{ __html: sectionSubTitle }}
          />
        )}
        {featuresList.length > 0 && (
          <div className={styles.CompleteFlexibilityWithCatalogueCommerceRow}>
            {featuresList.map((feature, index) => {
              const { cardTitle, cardDescription, cardImageUrl, cardImageUrlFallback, cardImageAlt, cardImageHeight, cardImageWidth } = feature;
              return (
                <div key={index} className={styles.CompleteFlexibilityWithCatalogueCommerceColumn}>
                  <Card key={index}
                    orientation={Orientation.HORIZONTAL}
                    imagePosition={{
                      mobile: Position.LEFT,
                      desktop: Position.LEFT
                    }}
                    alignment={{
                      justifyContent: FlexAlignment.FLEX_START,
                      alignItems: FlexAlignment.FLEX_START
                    }}
                    cardImage={{
                      className: styles.CataloguingPageCardImage,
                      imageUrl: cardImageUrl,
                      imageUrlFallback: cardImageUrlFallback,
                      imageAlt: cardImageAlt,
                      imageHeight: cardImageHeight,
                      imageWidth: cardImageWidth
                    }}
                    cardContent={{
                      className: styles.CataloguingPageCardContent,
                      title: (
                        cardTitle ? (
                          <Typography
                            variant={TypographyVariants.HEADING_4}
                            className={styles.CataloguingPageCardTitle}
                            style={{ fontSize: '23px', fontWeight: '700', fontFamily: TypographyFonts.AVENIR_NEXT, color: 'var(--green)' }}
                            tabletStyle={{ fontSize: '22px' }}
                            mobileStyle={{ fontSize: '21px' }}
                            dangerouslySetInnerHTML={{ __html: cardTitle }}
                          />
                        ) : null
                      ),
                      description: (
                        <Fragment>
                          {cardDescription && (
                            <Typography
                              variant={TypographyVariants.PARAGRAPH}
                              style={{ fontSize: '20px', color: 'var(--black)', fontFamily: TypographyFonts.AVENIR_NEXT, marginTop: '24px' }}
                              tabletStyle={{ fontSize: '19px' }}
                              mobileStyle={{ fontSize: '18px' }}
                              className={styles.CataloguingPageCardDescription}
                              dangerouslySetInnerHTML={{ __html: cardDescription }}
                            />
                          )}
                        </Fragment>
                      )
                    }}
                    className={styles.CataloguingPageCard}
                  />
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  const renderDigitalProductShowcaseContainer = () => {
    const { sectionTitle, sectionSubTitle, featuresList = [] } = digitalProductSection || {};
    return (
      <section className={`${styles.DigitalProductShowcaseContainer} page-section-padding`}>
        {sectionTitle && (
          <Typography
            variant={TypographyVariants.HEADING_2}
            style={{ fontSize: '42px', fontWeight: '700', fontFamily: TypographyFonts.LIBRE_BODONI, fontStyle: 'italic', color: 'var(--black)', marginTop: '24px', textAlign: 'center' }}
            tabletStyle={{ fontSize: '36px', marginTop: '12px' }}
            mobileStyle={{ fontSize: '30px', marginTop: '0px' }}
            dangerouslySetInnerHTML={{ __html: sectionTitle }}
          />
        )}
        {sectionSubTitle && (
          <Typography
            variant={TypographyVariants.HEADING_3}
            style={{ fontSize: '25px', fontFamily: TypographyFonts.AVENIR_NEXT, color: 'var(--green)', marginTop: '36px', marginBottom: '56px', textAlign: 'center' }}
            tabletStyle={{ fontSize: '23px', marginTop: '24px', marginBottom: '48px' }}
            mobileStyle={{ fontSize: '21px', marginTop: '24px', marginBottom: '36px' }}
            dangerouslySetInnerHTML={{ __html: sectionSubTitle }}
          />
        )}
        {featuresList.length > 0 && (
          <div className={styles.DigitalProductShowcaseRow}>
            {featuresList.map((feature, index) => {
              const { cardTitle, featuresList = [], cardImageUrl, cardImageUrlFallback, cardImageAlt, cardImageHeight, cardImageWidth } = feature;
              return (
                <div key={index} className={styles.DigitalProductShowcaseColumn}>
                  <Card
                    orientation={Orientation.VERTICAL}
                    imagePosition={{
                      mobile: Position.LEFT,
                      desktop: Position.LEFT
                    }}
                    alignment={{
                      justifyContent: FlexAlignment.FLEX_START,
                      alignItems: FlexAlignment.FLEX_START
                    }}
                    cardImage={{
                      className: styles.CataloguingPageCardImage,
                      imageUrl: cardImageUrl,
                      imageUrlFallback: cardImageUrlFallback,
                      imageAlt: cardImageAlt,
                      imageHeight: cardImageHeight,
                      imageWidth: cardImageWidth
                    }}
                    cardContent={{
                      className: styles.CataloguingPageCardContent,
                      title: (
                        cardTitle ? (
                          <Typography
                            variant={TypographyVariants.HEADING_3}
                            className={styles.ProductLayoutItemTitle}
                            style={{ fontSize: '24px', fontWeight: '700', color: 'var(--black)', marginTop: '12px' }}
                            tabletStyle={{ fontSize: '22px' }}
                            mobileStyle={{ fontSize: '20px' }}
                            dangerouslySetInnerHTML={{ __html: cardTitle }}
                          />
                        ) : null
                      ),
                      description: (
                        <div className={styles.CataloguingPageFeaturesContainer}>
                          {featuresList.length > 0 && (featuresList.map((feature, index) => {
                            const { cardImageUrl, cardImageHeight, cardImageWidth, cardDescription } = feature;
                            return (
                              <div key={index} className={styles.CataloguingPageFeatureListContainer}>
                                <div className={styles.CataloguingPageFeatureListItemContainer}>
                                  <img src={getUrlForAssets(cardImageUrl)} alt="feature icon" className={styles.CataloguingPageFeatureListItemIcon} loading={ImageLoading.LAZY} height={cardImageHeight || "100%"} width={cardImageWidth || "100%"} />
                                  {cardDescription && (
                                    <Typography
                                      variant={TypographyVariants.PARAGRAPH}
                                      className={styles.CataloguingPageFeatureListItemText}
                                      dangerouslySetInnerHTML={{ __html: cardDescription }}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          }))}
                        </div>
                      )
                    }}
                    className={styles.CataloguingPageCard}
                  />
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  const renderCreateYourOwnCustomLayoutContainer = () => {
    const { sectionTitle, featuresList = [] } = createYourOwnCustomLayoutSection || {};
    const leftColumnFeaturesList = featuresList.filter((feature) => feature.columnName === 'leftColumn');
    const centerColumnFeaturesList = featuresList.filter((feature) => feature.columnName === 'centerColumn');
    const rightColumnFeaturesList = featuresList.filter((feature) => feature.columnName === 'rightColumn');
    const renderBannerAndCatalogueSection = () => {
      return (
        <Fragment>
          {leftColumnFeaturesList[0] && (
            <div className={`${styles.CustomPageSectionContainer} ${styles.CustomPageSectionBannerContainer}`}>
              {leftColumnFeaturesList[0].cardImageUrl && (
                <img src={getUrlForAssets(leftColumnFeaturesList[0].cardImageUrl)} className={`${styles.CustomPageSectionArrow} ${styles.CustomPageBannerSectionArrow}`} loading={ImageLoading.LAZY} height={leftColumnFeaturesList[0].cardImageHeight || "100%"} width={leftColumnFeaturesList[0].cardImageWidth || "100%"} />
              )}
              {(leftColumnFeaturesList[0].cardTitle || leftColumnFeaturesList[0].cardDescription) && (
                <div className={`${styles.CustomPageSectionHeading} ${styles.CustomPageBannerSectionHeading}`}>
                  {leftColumnFeaturesList[0].cardTitle && (
                    <Typography
                      variant={TypographyVariants.HEADING_3}
                      style={{ fontSize: '24px', fontWeight: '700', marginTop: '-24px', marginBottom: '12px', paddingRight: '56px' }}
                      tabletStyle={{ fontSize: '21px', marginTop: '-12px', paddingRight: '36px' }}
                      mobileStyle={{ fontSize: '18px', marginTop: '0px', paddingRight: '12px' }}
                      dangerouslySetInnerHTML={{ __html: leftColumnFeaturesList[0].cardTitle }}
                    />
                  )}
                  {leftColumnFeaturesList[0].cardDescription && (
                    <Typography
                      variant={TypographyVariants.PARAGRAPH}
                      style={{ fontSize: '20px', paddingRight: '56px' }}
                      tabletStyle={{ fontSize: '18px', paddingRight: '36px' }}
                      mobileStyle={{ fontSize: '16px', paddingRight: '12px' }}
                      dangerouslySetInnerHTML={{ __html: leftColumnFeaturesList[0].cardDescription }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {leftColumnFeaturesList[1] && (
            <div className={`${styles.CustomPageSectionContainer} ${styles.CustomPageSectionCatalogueContainer}`}>
              {leftColumnFeaturesList[1].cardImageUrl && (
                <img src={getUrlForAssets(leftColumnFeaturesList[1].cardImageUrl)} className={`${styles.CustomPageSectionArrow} ${styles.CustomPageCatalogueSectionArrow}`} loading={ImageLoading.LAZY} height={leftColumnFeaturesList[1].cardImageHeight || "100%"} width={leftColumnFeaturesList[1].cardImageWidth || "100%"} />
              )}
              {(leftColumnFeaturesList[1].cardTitle || leftColumnFeaturesList[1].cardDescription) && (
                <div className={`${styles.CustomPageSectionHeading} ${styles.CustomPageCatalogueSectionHeading}`}>
                  {leftColumnFeaturesList[1].cardTitle && (
                    <Typography
                      variant={TypographyVariants.HEADING_3}
                      style={{ fontSize: '24px', fontWeight: '700', marginTop: '-24px', marginBottom: '12px', paddingRight: '56px' }}
                      tabletStyle={{ fontSize: '21px', marginTop: '-12px', paddingRight: '36px' }}
                      mobileStyle={{ fontSize: '18px', marginTop: '0px', paddingRight: '12px' }}
                      dangerouslySetInnerHTML={{ __html: leftColumnFeaturesList[1].cardTitle }}
                    />
                  )}
                  {leftColumnFeaturesList[1].cardDescription && (
                    <Typography
                      variant={TypographyVariants.PARAGRAPH}
                      style={{ fontSize: '20px', paddingRight: '56px' }}
                      tabletStyle={{ fontSize: '18px', paddingRight: '36px' }}
                      mobileStyle={{ fontSize: '16px', paddingRight: '12px' }}
                      dangerouslySetInnerHTML={{ __html: leftColumnFeaturesList[1].cardDescription }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </Fragment>
      );
    };
    const renderProductSection = () => {
      return (
        <Fragment>
          {rightColumnFeaturesList[0] && (
            <div className={`${styles.CustomPageSectionContainer} ${styles.CustomPageSectionProductContainer}`}>
              {rightColumnFeaturesList[0].cardImageUrl && (
                <img src={getUrlForAssets(rightColumnFeaturesList[0].cardImageUrl)} className={`${styles.CustomPageSectionArrow} ${styles.CustomPageProductSectionArrow}`} loading={ImageLoading.LAZY} height={rightColumnFeaturesList[0].cardImageHeight || "100%"} width={rightColumnFeaturesList[0].cardImageWidth || "100%"} />
              )}
              {(rightColumnFeaturesList[0].cardTitle || rightColumnFeaturesList[0].cardDescription) && (
                <div className={`${styles.CustomPageSectionHeading} ${styles.CustomPageProductSectionHeading}`}>
                  {rightColumnFeaturesList[0].cardTitle && (
                    <Typography
                      variant={TypographyVariants.HEADING_3}
                      style={{ fontSize: '24px', fontWeight: '700', marginTop: '-24px', marginBottom: '12px', paddingLeft: '56px' }}
                      tabletStyle={{ fontSize: '21px', marginTop: '-12px', paddingLeft: '36px' }}
                      mobileStyle={{ fontSize: '18px', marginTop: '0px', paddingLeft: '12px' }}
                      dangerouslySetInnerHTML={{ __html: rightColumnFeaturesList[0].cardTitle }}
                    />
                  )}
                  {rightColumnFeaturesList[0].cardDescription && (
                    <Typography
                      variant={TypographyVariants.PARAGRAPH}
                      style={{ fontSize: '20px', paddingLeft: '56px' }}
                      tabletStyle={{ fontSize: '18px', paddingLeft: '36px' }}
                      mobileStyle={{ fontSize: '16px', paddingLeft: '12px' }}
                      dangerouslySetInnerHTML={{ __html: rightColumnFeaturesList[0].cardDescription }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </Fragment>
      );
    };
    return (
      <section className={`${styles.CreateYourOwnCustomLayoutContainer} page-section-padding`}>
        {sectionTitle && (
          <Typography
            variant={TypographyVariants.HEADING_2}
            style={{ fontSize: '36px', fontFamily: TypographyFonts.LIBRE_BODONI, fontStyle: 'italic', textAlign: 'center', fontWeight: '500', marginBottom: "50px" }}
            tabletStyle={{ fontSize: '32px' }}
            mobileStyle={{ fontSize: '26px', paddingLeft: '36px', paddingRight: '36px' }}
            dangerouslySetInnerHTML={{ __html: sectionTitle }}
          />
        )}
        <div className={styles.CreateYourOwnCustomLayoutRow}>
          <div className={`${styles.CreateYourOwnCustomLayoutColumn} ${styles.CreateYourOwnCustomLayoutColumnFeatures}`}>
            {renderBannerAndCatalogueSection()}
            {renderProductSection()}
          </div>
          <div className={`${styles.CreateYourOwnCustomLayoutColumn} ${styles.CreateYourOwnCustomLayoutColumnBanner}`}>
            {centerColumnFeaturesList[0] && centerColumnFeaturesList[0].cardImageUrl && (
              <Image src={getUrlForAssets(centerColumnFeaturesList[0].cardImageUrl)} fallbackSrc={getUrlForAssets(centerColumnFeaturesList[0].cardImageUrlFallback)} alt="create-your-own-custom-layout" className={styles.CreateYourOwnCustomLayoutColumnBannerImage} height={centerColumnFeaturesList[0].cardImageHeight || '100%'} width={centerColumnFeaturesList[0].cardImageWidth || '100%'} />
            )}
          </div>
          <div className={`${styles.CreateYourOwnCustomLayoutColumn} ${styles.CreateYourOwnCustomLayoutColumnFeatures}`}>
            {renderBannerAndCatalogueSection()}
            {renderProductSection()}
          </div>
        </div>
      </section>
    );
  };

  const renderCustomThemesContainer = () => {
    const { sectionTitle, sectionSubTitle, featuresList = [] } = customThemesSection || {};
    return (
      <section className={`${styles.CustomThemesContainer} page-section-padding`}>
        {sectionTitle && (
          <Typography
            style={{ fontSize: '30px', fontWeight: '700', color: 'var(--green)', marginBottom: '18px', textAlign: 'center' }}
            tabletStyle={{ fontSize: '28px' }}
            mobileStyle={{ fontSize: '26px' }}
            dangerouslySetInnerHTML={{ __html: sectionTitle }}
          />
        )}
        {sectionSubTitle && (
          <Typography
            style={{ fontSize: '25px', fontWeight: '400', color: 'var(--black)', marginBottom: '56px', textAlign: 'center' }}
            tabletStyle={{ fontSize: '23px', marginBottom: '48px' }}
            mobileStyle={{ fontSize: '21px', marginBottom: '36px' }}
            dangerouslySetInnerHTML={{ __html: sectionSubTitle }}
          />
        )}
        {featuresList.length > 0 && (
          <div className={styles.CustomThemesList}>
            {featuresList.map((feature, index) => {
              const { cardImageUrl, cardImageHeight, cardImageWidth } = feature;
              return (
                <div key={index} className={`${styles.CustomThemesListItem} ${styles[`CustomThemesListItem${index + 1}`]}`}>
                  <img src={getUrlForAssets(cardImageUrl)} alt="custom-theme" className={styles.CustomThemesListItemImage} loading={ImageLoading.EAGER} height={cardImageHeight || "100%"} width={cardImageWidth || "100%"} />
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  const renderBeautyInSimplicityContainer = () => {
    const { sectionTitle, featuresList = [] } = beautyInSimplicitySection || {};
    const leftColumnFeaturesList = featuresList.filter((feature) => feature.columnName === 'leftColumn');
    const centerColumnFeaturesList = featuresList.filter((feature) => feature.columnName === 'centerColumn');
    const rightColumnFeaturesList = featuresList.filter((feature) => feature.columnName === 'rightColumn');
    const bottomColumnFeaturesList = featuresList.filter((feature) => feature.columnName === 'bottomColumn');
    const renderSubCatagoriesSection = () => {
      return (
        <Fragment>
          {leftColumnFeaturesList[0] && (
            <div className={`${styles.CustomPageSectionContainer} ${styles.CustomPageSectionSubCategoriesContainer}`}>
              {leftColumnFeaturesList[0].cardImageUrl && (
                <img src={getUrlForAssets(leftColumnFeaturesList[0].cardImageUrl)} className={`${styles.CustomPageSectionArrow} ${styles.CustomPageSubCategoriesSectionArrow}`} loading={ImageLoading.LAZY} height={leftColumnFeaturesList[0].cardImageHeight || "100%"} width={leftColumnFeaturesList[0].cardImageWidth || "100%"} />
              )}
              {(leftColumnFeaturesList[0].cardTitle || leftColumnFeaturesList[0].cardDescription) && (
                <div className={`${styles.CustomPageSectionHeading} ${styles.CustomPageSubCategoriesSectionHeading}`}>
                  {leftColumnFeaturesList[0].cardTitle && (
                    <Typography
                      variant={TypographyVariants.HEADING_3}
                      style={{ fontSize: '24px', fontWeight: '700', marginTop: '-24px', marginBottom: '12px', paddingRight: '56px' }}
                      tabletStyle={{ fontSize: '21px', marginTop: '-12px', paddingRight: '36px' }}
                      mobileStyle={{ fontSize: '18px', marginTop: '0px', paddingRight: '12px' }}
                      dangerouslySetInnerHTML={{ __html: leftColumnFeaturesList[0].cardTitle }}
                    />
                  )}
                  {leftColumnFeaturesList[0].cardDescription && (
                    <Typography
                      variant={TypographyVariants.PARAGRAPH}
                      style={{ fontSize: '20px', paddingRight: '56px' }}
                      tabletStyle={{ fontSize: '18px', paddingRight: '36px' }}
                      mobileStyle={{ fontSize: '16px', paddingRight: '12px' }}
                      dangerouslySetInnerHTML={{ __html: leftColumnFeaturesList[0].cardDescription }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </Fragment>
      );
    };
    const renderPowerfulSearchAndCustomActionButtonSection = () => {
      return (
        <Fragment>
          {rightColumnFeaturesList[0] && (
            <div className={`${styles.CustomPageSectionContainer} ${styles.CustomPageSectionPowerfulSearchContainer}`}>
              {rightColumnFeaturesList[0].cardImageUrl && (
                <img src={getUrlForAssets(rightColumnFeaturesList[0].cardImageUrl)} className={`${styles.CustomPageSectionArrow} ${styles.CustomPagePowerfulSearchSectionArrow}`} loading={ImageLoading.LAZY} height={rightColumnFeaturesList[0].cardImageheight || "100%"} width={rightColumnFeaturesList[0].cardImageWidth || "100%"} />
              )}
              {(rightColumnFeaturesList[0].cardTitle || rightColumnFeaturesList[0].cardDescription) && (
                <div className={`${styles.CustomPageSectionHeading} ${styles.CustomPagePowerfulSearchSectionHeading}`}>
                  {rightColumnFeaturesList[0].cardTitle && (
                    <Typography
                      variant={TypographyVariants.HEADING_3}
                      style={{ fontSize: '24px', fontWeight: '700', marginTop: '-24px', paddingLeft: '56px' }}
                      tabletStyle={{ fontSize: '21px', marginTop: '-12px', paddingLeft: '36px' }}
                      mobileStyle={{ fontSize: '18px', marginTop: '0px', paddingLeft: '12px' }}
                      dangerouslySetInnerHTML={{ __html: rightColumnFeaturesList[0].cardTitle }}
                    />
                  )}
                  {rightColumnFeaturesList[0].cardDescription && (
                    <Typography
                      variant={TypographyVariants.HEADING_3}
                      style={{ fontSize: '24px', fontWeight: '700', marginTop: '-24px', paddingLeft: '56px' }}
                      tabletStyle={{ fontSize: '21px', marginTop: '-12px', paddingLeft: '36px' }}
                      mobileStyle={{ fontSize: '18px', marginTop: '0px', paddingLeft: '12px' }}
                      dangerouslySetInnerHTML={{ __html: rightColumnFeaturesList[0].cardDescription }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {rightColumnFeaturesList[1] && (
            <div className={`${styles.CustomPageSectionContainer} ${styles.CustomPageSectionCustomActionButtonContainer}`}>
              {rightColumnFeaturesList[1].cardImageUrl && (
                <img src={getUrlForAssets(rightColumnFeaturesList[1].cardImageUrl)} className={`${styles.CustomPageSectionArrow} ${styles.CustomPageCustomActionButtonSectionArrow}`} loading={ImageLoading.LAZY} height={rightColumnFeaturesList[1].cardImageheight || "100%"} width={rightColumnFeaturesList[1].cardImageWidth || "100%"} />
              )}
              {(rightColumnFeaturesList[1].cardTitle || rightColumnFeaturesList[1].cardDescription) && (
                <div className={`${styles.CustomPageSectionHeading} ${styles.CustomPageCustomActionButtonSectionHeading}`}>
                  {rightColumnFeaturesList[1].cardTitle && (
                    <Typography
                      variant={TypographyVariants.HEADING_3}
                      style={{ fontSize: '24px', fontWeight: '700', marginTop: '-24px', paddingLeft: '56px' }}
                      tabletStyle={{ fontSize: '21px', marginTop: '-12px', paddingLeft: '36px' }}
                      mobileStyle={{ fontSize: '18px', marginTop: '0px', paddingLeft: '12px' }}
                      dangerouslySetInnerHTML={{ __html: rightColumnFeaturesList[1].cardTitle }}
                    />
                  )}
                  {rightColumnFeaturesList[1].cardDescription && (
                    <Fragment>
                      <Typography
                        variant={TypographyVariants.HEADING_3}
                        style={{ fontSize: '18px', fontWeight: '700', color: 'var(--blue)', textAlign: 'center' }}
                        tabletStyle={{ fontSize: '17px' }}
                        mobileStyle={{ fontSize: '16px' }}
                        dangerouslySetInnerHTML={{ __html: rightColumnFeaturesList[1].cardDescription }}
                      />
                    </Fragment>
                  )}
                </div>
              )}
            </div>
          )}
        </Fragment>
      );
    };
    const renderSimpleCheckoutSection = () => {
      return (
        <Fragment>
          {bottomColumnFeaturesList[0] && (
            <div className={`${styles.CustomPageSectionContainer} ${styles.CustomPageSectionSimpleCheckoutContainer}`}>
              {bottomColumnFeaturesList[0].cardImageUrl && (
                <div className={`${styles.BeautyInSimplicityColumn} ${styles.BeautyInSimplicityColumnFeatures}`}>
                  <img src={getUrlForAssets(bottomColumnFeaturesList[0].cardImageUrl)} className={`${styles.CustomPageSectionArrow} ${styles.CustomPageSimpleCheckoutSectionArrow}`} loading={ImageLoading.LAZY} height={bottomColumnFeaturesList[0].cardImageHeight || "100%"} width={bottomColumnFeaturesList[0].cardImageWidth || "100%"} />
                </div>
              )}
              {(bottomColumnFeaturesList[0].cardTitle && bottomColumnFeaturesList[0].cardDescription) && (
                <Fragment>
                  {bottomColumnFeaturesList[0].cardTitle && (
                    <div className={`${styles.BeautyInSimplicityColumn} ${styles.BeautyInSimplicityColumnFeatures}`}>
                      <Typography
                        variant={TypographyVariants.HEADING_3}
                        style={{ fontSize: '24px', fontWeight: '700', marginTop: '24px' }}
                        tabletStyle={{ fontSize: '21px', marginTop: '24px' }}
                        mobileStyle={{ fontSize: '18px', marginTop: '18px' }}
                        dangerouslySetInnerHTML={{ __html: bottomColumnFeaturesList[0].cardTitle }}
                      />
                    </div>
                  )}
                  {bottomColumnFeaturesList[0].cardDescription && (
                    <div className={`${styles.BeautyInSimplicityColumn} ${styles.BeautyInSimplicityColumnFeatures}`}>
                      <Typography
                        variant={TypographyVariants.PARAGRAPH}
                        style={{ fontSize: '22px', fontWeight: '400', textAlign: 'center', marginTop: '24px', marginBottom: '24px' }}
                        tabletStyle={{ fontSize: '20px', marginTop: '12px' }}
                        mobileStyle={{ fontSize: '18px', textAlign: 'left', marginTop: '12px' }}
                        dangerouslySetInnerHTML={{ __html: bottomColumnFeaturesList[0].cardDescription }}
                      />
                    </div>
                  )}
                </Fragment>
              )}
            </div>
          )}
        </Fragment>
      );
    };
    return (
      <section className={`${styles.BeautyInSimplicityContainer} page-section-padding`}>
        {(sectionTitle) && (
          <Typography
            variant={TypographyVariants.HEADING_3}
            style={{ fontSize: '30px', fontWeight: '700', color: 'var(--green)', textAlign: 'center', marginBottom: '56px' }}
            tabletStyle={{ fontSize: '28px', marginBottom: '48px' }}
            mobileStyle={{ fontSize: '26px', marginBottom: '36px', paddingLeft: '36px', paddingRight: '36px' }}
            dangerouslySetInnerHTML={{ __html: sectionTitle }}
          />
        )}
        <div className={styles.BeautyInSimplicityRowContainer}>
          <div className={styles.BeautyInSimplicityRow}>
            <div className={`${styles.BeautyInSimplicityColumn} ${styles.BeautyInSimplicityColumnFeatures}`}>
              {renderSubCatagoriesSection()}
              {renderPowerfulSearchAndCustomActionButtonSection()}
            </div>
            <div className={`${styles.BeautyInSimplicityColumn} ${styles.BeautyInSimplicityColumnBanner}`}>
              {centerColumnFeaturesList[0] && centerColumnFeaturesList[0].cardImageUrl && (
                <Image src={getUrlForAssets(centerColumnFeaturesList[0].cardImageUrl)} fallbackSrc={getUrlForAssets(centerColumnFeaturesList[0].cardImageUrlFallback)} alt="create-your-own-custom-layout" className={styles.BeautyInSimplicityColumnBannerImage} height={centerColumnFeaturesList[0].cardImageHeight || '100%'} width={centerColumnFeaturesList[0].cardImageWidth || '100%'} />
              )}
            </div>
            <div className={`${styles.BeautyInSimplicityColumn} ${styles.BeautyInSimplicityColumnFeatures}`}>
              {renderSubCatagoriesSection()}
              {renderPowerfulSearchAndCustomActionButtonSection()}
            </div>
          </div>
          <div className={styles.BeautyInSimplicityRow}>
            {renderSimpleCheckoutSection()}
          </div>
        </div>
      </section>
    );
  };

  const renderTalkToAnExpertContainer = () => {
    const { cardImageUrl, cardImageUrlFallback, cardImageAlt, cardImageHeight, cardImageWidth, cardTitle, cardDescription, buttonsList = [] } = expertSection || {};
    return (
      <section className={`${styles.TalkToAnExpertContainer} page-section-padding`}>
        <Card
          orientation={Orientation.HORIZONTAL}
          imagePosition={{
            mobile: Position.LEFT,
            desktop: Position.RIGHT
          }}
          alignment={{
            justifyContent: FlexAlignment.FLEX_START,
            alignItems: FlexAlignment.CENTER
          }}
          cardImage={{
            className: styles.CataloguingPageCardImage,
            imageUrl: cardImageUrl,
            imageUrlFallback: cardImageUrlFallback,
            imageAlt: cardImageAlt,
            imageHeight: cardImageHeight,
            imageWidth: cardImageWidth
          }}
          cardContent={{
            className: styles.CataloguingPageCardContent,
            title: (
              cardTitle ? (
                <Typography
                  variant={TypographyVariants.HEADING_2}
                  className={styles.CataloguingPageCardTitle}
                  style={{ fontSize: '36px', fontWeight: '700', fontFamily: TypographyFonts.LIBRE_BODONI, fontStyle: 'italic', color: 'var(--black)' }}
                  tabletStyle={{ fontSize: '32px' }}
                  mobileStyle={{ fontSize: '26px' }}
                  dangerouslySetInnerHTML={{ __html: cardTitle }}
                />
              ) : null
            ),
            description: (
              <Fragment>
                {cardDescription && (
                  <Typography
                    variant={TypographyVariants.HEADING_3}
                    style={{ fontSize: '26px', color: 'var(--black)', marginTop: '24px' }}
                    tabletStyle={{ fontSize: '24px' }}
                    mobileStyle={{ fontSize: '22px' }}
                    className={styles.CataloguingPageCardDescription}
                    dangerouslySetInnerHTML={{ __html: cardDescription }}
                  />
                )}
                {buttonsList.length > 0 && (
                  <div className={styles.CataloguingPageCardButtonsContainer}>
                    {buttonsList.map((button, index) => {
                      const { type, htmlType, linkUrl, openInNewTab, text, onClick } = button;
                      return (
                        <Button key={index} type={type} htmlType={htmlType} className={styles.CataloguingPageCardButton} linkUrl={linkUrl} openInNewTab={openInNewTab} onClick={onClick}>{text}</Button>
                      );
                    })}
                  </div>
                )}
              </Fragment>
            )
          }}
          className={styles.CataloguingPageCard}
        />
      </section>
    );
  };

  return (
    <main className={styles.CataloguingPageContainer}>
      {renderCreateBeautifulCatalogueContainer()}
      <PageDivider />
      {renderCompleteFlexibilityWithCatalogueCommerceContainer()}
      <PageDivider />
      {renderDigitalProductShowcaseContainer()}
      {renderCreateYourOwnCustomLayoutContainer()}
      {renderCustomThemesContainer()}
      {renderBeautyInSimplicityContainer()}
      <PageDivider />
      {renderTalkToAnExpertContainer()}
    </main>
  );
};

export default CataloguingPage;
